/**
 * Environment, validated once at module load (00-contracts §2.2).
 *
 * The error thrown here is deliberately **untranslated**: it is a developer/operator fault that
 * happens before i18next exists, and a mistranslated boot failure helps nobody. Every other
 * user-visible string in this app goes through i18next.
 */
import { z } from 'zod';

const booleanFlag = z
  .enum(['true', 'false'])
  .default('false')
  .transform((value) => value === 'true');

const envSchema = z.object({
  /**
   * Utelatt: API-et ligger på nettsidens EGEN adresse (`/v1` bak Pages-proxyen, sak 240). Da virker
   * dev.tvitvi.com og hver `*.pages.dev`-forhåndsvisning med samme bygg, og nettleseren sender
   * ingen CORS-preflight. Lokalt står den satt, fordi API-et der er en annen port.
   */
  VITE_API_URL: z.url().optional(),
  VITE_SUPABASE_URL: z.url(),
  VITE_SUPABASE_ANON_KEY: z.string().min(1),
  VITE_OAUTH_GOOGLE_ENABLED: booleanFlag,
  VITE_OAUTH_MICROSOFT_ENABLED: booleanFlag,
  VITE_APP_ENV: z.enum(['local', 'ci', 'staging', 'production']).default('local'),
  /**
   * MCP-adressen AI-siden ber brukeren lime inn (B5). Offentlig og lik for alle, så den hører i
   * bygget og ikke i et API-svar. Uten verdi: dev-adressen, som er den eneste som finnes ennå.
   */
  VITE_MCP_URL: z.url().optional(),
});

const parsed = envSchema.safeParse(import.meta.env);

if (!parsed.success) {
  const detail = parsed.error.issues
    .map((issue) => `  ${issue.path.join('.') || '(root)'}: ${issue.message}`)
    .join('\n');
  throw new Error(
    `Invalid frontend environment. Copy apps/web/.env.example to apps/web/.env and fill it in.\n${detail}`,
  );
}

const raw = parsed.data;

export const env = {
  /** Trailing slash stripped, so every call site can concatenate without thinking about it. */
  apiUrl: (raw.VITE_API_URL ?? window.location.origin).replace(/\/+$/, ''),
  supabaseUrl: raw.VITE_SUPABASE_URL,
  supabaseAnonKey: raw.VITE_SUPABASE_ANON_KEY,
  oauth: {
    google: raw.VITE_OAUTH_GOOGLE_ENABLED,
    microsoft: raw.VITE_OAUTH_MICROSOFT_ENABLED,
  },
  /**
   * Uten skråstrek til slutt: Claude krever at adressen er nøyaktig den serveren oppgir. Railway-adressen
   * til mcp.dev.tvitvi.com har et domene (279.9 a); da byttes den her eller med VITE_MCP_URL.
   */
  mcpUrl: (raw.VITE_MCP_URL ?? 'https://mcp-production-a7e4.up.railway.app').replace(/\/+$/, ''),
  appEnv: raw.VITE_APP_ENV,
  isProduction: raw.VITE_APP_ENV === 'production',
} as const;
